/**
 * `/artifacts`: everything replies have made, across every chat, the most
 * recently changed first (`GET /artifacts`, a page at a time). Each opens in
 * its chat, beside it (`/chat/<id>?artifact=<id>`).
 */

import { useCallback, useEffect, useState } from 'react';
import { listLibrary, type ArtifactLibrary } from '../api';
import { ds } from '../ds';
import { Link } from '../router';
import { MainHeader, useShell } from '../components/Shell';
import { ARTIFACT_ICON, ARTIFACT_KIND_LABEL } from '../components/ArtifactTile';
import { relativeTime } from '../components/settings/format';
import { Ph } from '../components/ui';

type Entry = ArtifactLibrary['artifacts'][number];

function Row({ entry }: { entry: Entry }) {
  const shared = entry.share.level !== 'private';
  return (
    <Link
      to={`/chat/${entry.chatId}?artifact=${entry.id}`}
      className="hov-row"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        minHeight: 56,
        padding: '8px 12px',
        borderRadius: 'var(--radius-app-card)',
        color: 'inherit',
        textDecoration: 'none'
      }}
    >
      <span
        style={{
          width: 36,
          height: 36,
          flex: 'none',
          display: 'grid',
          placeItems: 'center',
          borderRadius: 'var(--radius-sm)',
          background: 'var(--primary-soft)',
          color: 'var(--primary-text)'
        }}
      >
        <Ph name={ARTIFACT_ICON[entry.kind]} size={17} />
      </span>
      <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-strong)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {entry.title}
        </span>
        <span style={{ fontSize: 12.5, color: 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {ARTIFACT_KIND_LABEL[entry.kind]} · {entry.chatTitle || 'Untitled chat'}
          {entry.version > 1 ? ` · version ${entry.version}` : ''}
        </span>
      </span>
      {shared ? <Ph name="share-network" size={15} color="var(--text-faint)" style={{ flex: 'none' }} /> : null}
      <span style={{ flex: 'none', fontSize: 12.5, color: 'var(--text-faint)' }}>{relativeTime(entry.updatedAt)}</span>
    </Link>
  );
}

export function ArtifactsPage() {
  const { narrow } = useShell();
  const { Button } = ds();
  const [entries, setEntries] = useState<Entry[] | null>(null);
  const [next, setNext] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = useCallback((before: string | null) => {
    setLoading(true);
    setFailed(false);
    listLibrary(before)
      .then((page) => {
        setEntries((was) => (before && was ? [...was, ...page.artifacts] : page.artifacts));
        setNext(page.next);
      })
      .catch(() => setFailed(true))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => load(null), [load]);

  return (
    <>
      <MainHeader>
        <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-strong)' }}>Artifacts</span>
      </MainHeader>
      <div className="scroll" style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        <div style={{ maxWidth: 760, margin: '0 auto', padding: narrow ? '4px 8px 32px' : '4px 20px 40px', display: 'flex', flexDirection: 'column', gap: 2 }}>
          {entries === null && !failed ? (
            Array.from({ length: 6 }, (_, i) => (
              <div key={i} className="pulse" style={{ height: 52, margin: '2px 0', borderRadius: 'var(--radius-app-card)', background: 'var(--app-panel-sunken)' }} />
            ))
          ) : entries && entries.length === 0 ? (
            <div style={{ padding: '48px 16px', textAlign: 'center', fontSize: 14, color: 'var(--text-muted)' }}>
              Nothing yet. Ask for a page, a chart or a document in any chat.
            </div>
          ) : (
            (entries ?? []).map((e) => <Row key={e.id} entry={e} />)
          )}
          {failed ? (
            <div style={{ padding: 16, fontSize: 13.5, color: 'var(--text-muted)', textAlign: 'center' }}>
              Artifacts did not load.{' '}
              <button type="button" className="bare" onClick={() => load(entries?.length ? next : null)} style={{ fontSize: 13.5, color: 'var(--text-link)' }}>
                Retry
              </button>
            </div>
          ) : next ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 12 }}>
              <Button variant="ghost" density="app" size="sm" disabled={loading} onClick={() => load(next)}>
                Show more
              </Button>
            </div>
          ) : null}
        </div>
      </div>
    </>
  );
}
